/**
 * @copyright Copyright 2019 Epic Systems Corporation
 * @file useAsyncInner internal helper function
 * @author Roland Scott
 * @module react-async-hook\internal\use-async\use-async-inner
 */

import { ComponentType, useReducer, useEffect } from "react";
import { IAsyncFunction, TParameters, rootReducer } from "../";
import { IAsyncPlaceholderProps, Executor, Ignorer } from "../../";
import { buildPlaceholder } from "./build-placeholder";
import { useTrackerRefs } from "./use-tracker-refs";
import { initializeTrackerLifecycle } from "./initialize-tracker-lifecycle";
import { AsyncPlaceholder, IAsyncState, IErrorHandler, ISuccessHandler } from "../../types";

/**
 * Construct the return values for the useAsync hook
 * @param asyncOperation The async operation
 * @param executeImmediately Whether to execute on mount
 * @param Placeholder A placeholder component. It will be bound to the state of the async operation and
 * returned so that it can be used as a UI placeholder while the async operation is in progress.
 * @param initialArguments Initial arguments when executing on mount
 * @param onFailure Failure callback
 * @param onSuccess Success callback
 * @param displayName Async display name for debugging
 */
export function useAsyncInner<
	TAsync extends IAsyncFunction,
	TPlaceholderProps extends IAsyncPlaceholderProps
>(
	asyncOperation: TAsync,
	executeImmediately: boolean,
	Placeholder?: ComponentType<TPlaceholderProps>,
	initialArguments?: TParameters<TAsync>,
	onFailure?: IErrorHandler<TAsync>,
	onSuccess?: ISuccessHandler<TAsync>,
	displayName?: string,
):
	| [IAsyncState, Executor<TAsync>, Ignorer]
	| [AsyncPlaceholder<TPlaceholderProps>, Executor<TAsync>, Ignorer, IAsyncState] {
	// Get the "instance variables"
	const [
		isInitializedRef,
		executeRef,
		ignoreRef,
		updateStateRef,
		executeCallbacksRef,
		nextIdRef,
		asyncFinishersRef,
		cleanupRef,
		stateRef,
		wrappedPlaceholderRef,
	] = useTrackerRefs<TAsync, TPlaceholderProps>();

	// Save the current state to a ref for anyone who needs to access it.
	const [state, dispatch] = useReducer(rootReducer, { ids: [], executions: {} });
	stateRef.current = state as any;

	// Initialize tracking on the first render.
	if (!isInitializedRef.current) {
		cleanupRef.current = initializeTrackerLifecycle(
			asyncOperation,
			executeImmediately,
			dispatch,
			asyncFinishersRef,
			executeRef,
			executeCallbacksRef,
			ignoreRef,
			nextIdRef,
			updateStateRef,
			initialArguments,
			onFailure,
			onSuccess,
		);
		wrappedPlaceholderRef.current = buildPlaceholder(stateRef, Placeholder, displayName);
		isInitializedRef.current = true;
	}

	// Register the cleanup function for when we unmount.
	useEffect(() => cleanupRef.current, []);

	return Placeholder
		? ([
				wrappedPlaceholderRef.current,
				executeRef.current,
				ignoreRef.current,
				state as any,
				// tslint:disable-next-line: indent
		  ] as [
				AsyncPlaceholder<TPlaceholderProps>,
				Executor<TAsync>,
				Ignorer,
				IAsyncState,
				// tslint:disable-next-line: indent
		  ])
		: ([state as any, executeRef.current, ignoreRef.current] as [
				IAsyncState,
				Executor<TAsync>,
				Ignorer,
				// tslint:disable-next-line: indent
		  ]);
}
